import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {getShiftStatus} from '@features/dashboard/screens/DashboardScreen/DashboardController';
import {showSessionExpiredMessage} from '@shared/store/errorSlice';
import {SHIFT_INACTIVE_STATUS_FLAG} from '@features/auth/constants/authConstants';

/**
 * Returns a `checkShiftStatus()` guard function that must be awaited before
 * performing any protected action (indirect activity, begin/resume repair,
 * new work order, new repair, tab switching).
 *
 * On every call it fetches the live shift status indicator for the currently
 * selected technician.  If the API returns statusFlag === 2 (not in shift) the
 * session-expired modal is shown (reusing the existing `showSessionExpiredMessage`
 * Redux action) and the function returns `false` so the caller can bail out.
 * Otherwise it returns `true` and the action may proceed.
 */
export const useShiftStatusGuard = () => {
  const dispatch = useDispatch();
  const {selectedTechnician} = useSelector(
    (state: RootState) => state.technician,
  );

  /**
   * Calls the shift status indicator API for the active technician.
   * @returns `true` if the action may proceed, `false` if it must be blocked.
   */
  const checkShiftStatus = async (): Promise<boolean> => {
    const techId = Number(selectedTechnician?.selectedTechnicianId);
    if (!techId) {
      // No technician selected — do not block; let the downstream API surface the error.
      return true;
    }
    try {
      const statusData = await getShiftStatus(techId);
      if (statusData?.statusFlag === SHIFT_INACTIVE_STATUS_FLAG) {
        dispatch(showSessionExpiredMessage());
        return false;
      }
    } catch {
      // If the status check API itself fails, allow the action to proceed so a
      // transient network error does not permanently lock the technician out.
    }
    return true;
  };

  return {checkShiftStatus};
};